import React, { useEffect, useState } from 'react';
import Joyride, { STATUS, EVENTS, ACTIONS } from 'react-joyride';
import { useOnboarding } from '../contexts/onboarding-context';
import { useTheme } from '@mui/material/styles';
import { Button, Box, Typography, Paper, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ViewInArOutlinedIcon from '@mui/icons-material/ViewInArOutlined';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import IosShareIcon from '@mui/icons-material/IosShare';
import EditIcon from '@mui/icons-material/Edit';

// Custom Tooltip Component with MUI components
const CustomTooltip = ({
    step,
    primaryProps,
    closeProps,
    tooltipProps,
    isLastStep,
}) => {    
    return (
        <Paper
            elevation={4}
            {...tooltipProps}
            sx={{
                p: 2,
                borderRadius: 2,
                maxWidth: 360,
                bgcolor: 'background.paper',
                position: 'relative',
            }}
        >
            <IconButton
                {...closeProps}
                size="small"
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: 'text.secondary',
                }}
                aria-label="close"
            >
                <CloseIcon fontSize="small" />
            </IconButton>
            
            <Box sx={{ mr: 3 }}>
                {step.title && (
                    <Typography variant="h6" gutterBottom>
                        {step.title}
                    </Typography>
                )}
                {typeof step.content === 'string' ? (
                    <Typography variant="body2" color="text.secondary">
                        {step.content}
                    </Typography>
                ) : (
                    step.content
                )}
            </Box>
            
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                    {...primaryProps}
                    variant="contained"
                    color="primary"
                    size="medium"
                    endIcon={isLastStep ? <ThumbUpIcon /> : null}
                >
                    {isLastStep ? 'Got it' : 'Next'}
                </Button>
            </Box>
        </Paper>
    );
};

export function OnboardingHints() {
    const theme = useTheme();
    const { 
        shouldShowMyModelsHint, 
        clearMyModelsHint,
        shouldShowUploadPhotosHint,
        clearUploadPhotosHint,
        hasViewedHint
    } = useOnboarding();
    
    const [run, setRun] = useState(false);
    const [stepIndex, setStepIndex] = useState(0);
    const [steps, setSteps] = useState([]);

    // Trigger hints when flags are set
    useEffect(() => {
        if (shouldShowMyModelsHint && !hasViewedHint('firstModelCreated')) {
            // Define the My Models hint steps
            const myModelsSteps = [
                {
                    target: '[data-hint="user-menu-button"]',  // Target the hamburger menu
                    content: (
                        <div>
                            <h3 style={{ marginTop: 0 }}>Model Saved!</h3>
                            <p>Your custom model has been generated and saved!</p>
                            <p>Click this menu to find <strong>My Models</strong> where all your creations are stored. All future customizations will be saved in the model automatically.</p>
                            <p style={{ marginBottom: 0 }}>You can view, edit, and manage your saved designs anytime!</p>
                        </div>
                    ),
                    placement: 'bottom-end',
                    disableBeacon: true,
                    spotlightClicks: true,
                    styles: {
                        options: {
                            primaryColor: theme.palette.primary.main,
                        }
                    }
                },
                {
                    target: '[data-hint="model-tools-bar"]',  // Target the top bar area with name and toolbar
                    content: (
                        <div>
                            <h3 style={{ marginTop: 0 }}>Explore Your Model Tools!</h3>
                            <p>Your saved design comes with powerful features:</p>
                            <ul style={{ marginLeft: '1.2em' }}>
                                <li><strong><EditIcon style={{ fontSize: '1em', verticalAlign: 'middle' }} /> Design Name</strong> - Click the name (top-left) to create a unique title for tracking different configurations</li>
                                <li><strong><RestartAltIcon style={{ fontSize: '1em', verticalAlign: 'middle' }} /> Reset View</strong> - Return camera to the original position</li>
                                <li><strong><IosShareIcon style={{ fontSize: '1em', verticalAlign: 'middle' }} /> Share</strong> - Copy link to share this design with anyone</li>
                                <li><strong><ViewInArOutlinedIcon style={{ fontSize: '1em', verticalAlign: 'middle' }} /> View in AR</strong> - Use your mobile device to see the model in your space</li>
                            </ul>
                            <p style={{ marginBottom: 0 }}>These tools help you visualize, customize, and share your perfect woodworking project!</p>
                        </div>
                    ),
                    placement: 'bottom',
                    disableBeacon: true,
                    spotlightClicks: true,
                    styles: {
                        options: {
                            primaryColor: theme.palette.primary.main,
                        }
                    }
                },
                {
                    target: '[data-hint="customizer-button"]',  // Target the Customizer button
                    content: (
                        <div>
                            <h3 style={{ marginTop: 0 }}>Customize Your Model!</h3>
                            <p>Click this <strong>Customizer</strong> button to modify dimensions, options, and materials</p>
                            <p>All changes you make through the Customizer will be automatically updated to this design once built - no need to worry about losing your work!</p>
                        </div>
                    ),
                    placement: 'top',
                    disableBeacon: true,
                    spotlightClicks: true,
                    styles: {
                        options: {
                            primaryColor: theme.palette.primary.main,
                        }
                    }
                }
            ];
            
            setSteps(myModelsSteps);
            setRun(true);
        } else if (shouldShowUploadPhotosHint && !hasViewedHint('uploadPhotosIntro')) {
            // Check if desktop toolbar is available, otherwise use mobile menu
            const isDesktopToolbar = document.querySelector('[data-hint="plan-toolbar"]');
            const target = isDesktopToolbar ? '[data-hint="plan-toolbar"]' : '[data-hint="plan-menu-button"]';
            
            // Define the Upload Photos hint steps
            const uploadPhotosSteps = [
                {
                    target: target,
                    content: (
                        <div>
                            <h3 style={{ marginTop: 0 }}>Show Off Your Build!</h3>
                            <p>Once you build your project, we'd love to see your creation!</p>
                            <p>Your photos help inspire and guide other makers in the community.</p>
                            {isDesktopToolbar ? (
                                <p>Use the <strong>Upload Photos</strong> button in this toolbar to share your work.</p>
                            ) : (
                                <p>Click this menu to <strong>upload build photos</strong> and share your work.</p>
                            )}
                            <p style={{ marginBottom: 0 }}>You can also use the <strong>Model Details</strong> button to revisit the interactive model anytime to review your design!</p>
                        </div>
                    ),
                    placement: isDesktopToolbar ? 'top' : 'bottom-end',
                    disableBeacon: true,
                    spotlightClicks: true,
                    styles: {
                        options: {
                            primaryColor: theme.palette.primary.main,
                        }
                    }
                }
            ];
            
            setSteps(uploadPhotosSteps);
            setRun(true);
        }
    }, [shouldShowMyModelsHint, shouldShowUploadPhotosHint, theme.palette.primary.main]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleJoyrideCallback = (data) => {
        const { status, type, action, index } = data;

        // Handle tour completion (only when finished, skipped, or closed)
        if (status === STATUS.FINISHED || 
            status === STATUS.SKIPPED || 
            action === ACTIONS.CLOSE || 
            type === EVENTS.TOUR_END) {
            // Mark the hint as viewed based on the target
            if (steps.length > 0) {
                if (steps[0].target === '[data-hint="user-menu-button"]') {
                    clearMyModelsHint();
                } else if (steps[0].target === '[data-hint="plan-menu-button"]' || steps[0].target === '[data-hint="plan-toolbar"]') {
                    clearUploadPhotosHint();
                }
            }
            setRun(false);
            setStepIndex(0);
            setSteps([]);
        }
        // Handle step progression
        else if (type === EVENTS.STEP_AFTER) {
            setStepIndex(index + (action === ACTIONS.PREV ? -1 : 1));
        }
    };

    // Handle keyboard navigation
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (!run) return;
            
            if (event.key === 'Enter') {
                event.preventDefault();
                const isLastStep = stepIndex === steps.length - 1;
                
                if (isLastStep) {
                    // Complete the tutorial on last step
                    if (steps.length > 0) {
                        if (steps[0].target === '[data-hint="user-menu-button"]') {
                            clearMyModelsHint();
                        } else if (steps[0].target === '[data-hint="plan-menu-button"]' || steps[0].target === '[data-hint="plan-toolbar"]') {
                            clearUploadPhotosHint();
                        }
                    }
                    setRun(false);
                    setStepIndex(0);
                    setSteps([]);
                } else {
                    // Advance to next step
                    setStepIndex(stepIndex + 1);
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [run, stepIndex, steps, clearMyModelsHint, clearUploadPhotosHint]);

    // Minimal styles for Joyride - most styling is now handled by MUI components
    const joyrideStyles = {
        options: {
            arrowColor: theme.palette.background.paper,
            overlayColor: 'rgba(0, 0, 0, 0.5)',
            spotlightShadow: '0 0 15px rgba(0, 0, 0, 0.5)',
            zIndex: 10000,
        },
        spotlight: {
            borderRadius: theme.shape.borderRadius,
        },
        overlay: {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
        }
    };

    return (
        <Joyride
            steps={steps}
            run={run}
            stepIndex={stepIndex}
            continuous={true}
            showProgress={steps.length > 1}
            showSkipButton={false}
            disableCloseOnEsc={false}
            disableOverlayClose={false}
            styles={joyrideStyles}
            callback={handleJoyrideCallback}
            tooltipComponent={CustomTooltip}
            floaterProps={{
                disableAnimation: false,
                disableFlip: false,
            }}
        />
    );
}